import { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { ICMessage, shouldDeliverMessage, subscribeToAllMessages } from '../../services/instantConnectService';

const IC_NOTIFICATION_MODE_KEY = 'instantConnect:notificationOnly';

export function getNotificationOnly(): boolean {
  try {
    return localStorage.getItem(IC_NOTIFICATION_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setNotificationOnly(val: boolean): void {
  try {
    localStorage.setItem(IC_NOTIFICATION_MODE_KEY, val ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent('ic-notification-mode-changed', { detail: val }));
  } catch {
    // ignore
  }
}

/**
 * Invisible component mounted in Layout.tsx. Listens for new Instant Connect
 * voice messages and auto-plays them (unless user has toggled notification-only
 * mode). Only active for users with `instantConnectAccess`.
 */
export const InstantConnectListener: React.FC = () => {
  const { currentUser } = useAuth();
  const isMasterAdmin = !!currentUser?.roles?.includes(UserRole.MasterAdmin);
  const hasAccess = !!currentUser?.instantConnectAccess || isMasterAdmin;

  const playedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!hasAccess || !currentUser) return;

    const unsub = subscribeToAllMessages((messages: ICMessage[]) => {
      const notificationOnly = getNotificationOnly();

      for (const msg of messages) {
        if (msg.senderId === currentUser.uid) continue;
        if (playedIdsRef.current.has(msg.id)) continue;

        if (!shouldDeliverMessage(msg, currentUser.uid)) continue;

        playedIdsRef.current.add(msg.id);

        if (notificationOnly) {
          showNotification(msg);
        } else {
          playAudio(msg);
        }
      }
    });

    return () => unsub();
  }, [hasAccess, currentUser]);

  if (!hasAccess) return null;
  return null;
};

function playAudio(msg: ICMessage): void {
  try {
    const audio = new Audio(msg.audioUrl);
    audio.volume = 0.8;
    audio.play().catch(() => {
      showNotification(msg);
    });
  } catch {
    showNotification(msg);
  }
}

function showNotification(msg: ICMessage): void {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(`Instant Connect: ${msg.senderName}`, {
      body: `Voice message (${msg.duration}s)`,
      icon: '/favicon.ico',
      tag: `ic-${msg.id}`,
    });
  }
}
